import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import type { AttributeDef, TaskUI } from '@flexiplanner/types';
import { taskToUI } from '@flexiplanner/types';
import { api } from '@/lib/api';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface ActivitySessionLoggerProps {
  task: TaskUI | null;
  open: boolean;
  onClose: () => void;
  onLogged: (task: TaskUI) => void;
}

export function ActivitySessionLogger({ task, open, onClose, onLogged }: ActivitySessionLoggerProps) {
  const { toast } = useToast();
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [completionPercent, setCompletionPercent] = useState(0);
  const [emotionWord, setEmotionWord] = useState('');
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);
  const [customAttributes, setCustomAttributes] = useState<Record<string, unknown>>({});
  const [flowDepth, setFlowDepth] = useState<number | null>(null);
  const [flowNote, setFlowNote] = useState('');
  const [notes, setNotes] = useState('');
  const [actualState, setActualState] = useState({
    productivityInvestment: null as number | null,
    rejuvenationInvestment: null as number | null,
    productivityReturn: null as number | null,
    rejuvenationReturn: null as number | null,
    resistance: null as number | null,
    focusLevel: null as number | null,
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    const end = new Date();
    const start = new Date(end.getTime() - Math.max(5, task.estimatedDuration || 30) * 60000);
    setStartedAt(toDateTimeLocal(start));
    setEndedAt(toDateTimeLocal(end));
    setCompletionPercent(Math.max(0, Math.min(100, task.completionPercent ?? 0)));
    setCustomAttributes(task.customAttributes || {});
    setFlowDepth(null);
    setFlowNote('');
    setNotes('');
    setActualState({
      productivityInvestment: null,
      rejuvenationInvestment: null,
      productivityReturn: null,
      rejuvenationReturn: null,
      resistance: null,
      focusLevel: null,
    });
  }, [open, task?.id]);

  useEffect(() => {
    if (!open || !task) return;
    let cancelled = false;
    async function loadAttributes() {
      const response = await api.user.getAttributes();
      if (!cancelled && response.success && Array.isArray(response.data)) {
        setAttributes(response.data);
        const emotionAttr = response.data.find(isEmotionAttribute);
        const taskCustomAttributes = task?.customAttributes || {};
        setEmotionWord(String(getEmotionValue(taskCustomAttributes, emotionAttr) || ''));
      }
    }
    loadAttributes().catch(error => console.error('Failed to load activity session attributes:', error));
    return () => {
      cancelled = true;
    };
  }, [open, task]);

  const emotionAttribute = useMemo(() => attributes.find(isEmotionAttribute), [attributes]);
  const sessionAttributes = useMemo(
    () => attributes.filter(attr => !isEmotionAttribute(attr)),
    [attributes]
  );

  const durationMinutes = useMemo(() => {
    const start = new Date(startedAt);
    const end = new Date(endedAt);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) return null;
    return Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));
  }, [startedAt, endedAt]);

  async function handleSubmit() {
    if (!task || !durationMinutes) return;
    setIsSaving(true);
    try {
      const finalCustomAttributes = {
        ...customAttributes,
        ...(emotionAttribute ? { [emotionAttribute.attr_id]: emotionWord } : {}),
        emotion_word: emotionWord || null,
      };
      const response = await api.training.logActivitySession({
        taskId: task.id,
        startedAt: new Date(startedAt).toISOString(),
        endedAt: new Date(endedAt).toISOString(),
        actualDurationMinutes: durationMinutes,
        completionBefore: task.completionPercent ?? 0,
        completionPercent,
        completed: completionPercent >= 100,
        emotionWord,
        customAttributes: finalCustomAttributes,
        notes,
        actualState,
        flowMarkers:
          flowDepth != null || flowNote.trim()
            ? [
                {
                  timestamp: new Date(endedAt).toISOString(),
                  label: 'Flow state',
                  depth: flowDepth,
                  note: flowNote || null,
                },
              ]
            : [],
      });
      if (!response.success) throw new Error(response.error?.message || 'Could not log session');
      const updatedTask = taskToUI((response.data as any).task);
      onLogged(updatedTask);
      toast({
        title: 'Activity session logged',
        description: `${durationMinutes} minutes recorded. Completion is now ${completionPercent}%.`,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Could not log session',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  function updateAttributeValue(attr: AttributeDef, rawValue: string) {
    const value = isNumericAttribute(attr) && rawValue !== '' ? Number(rawValue) : rawValue;
    setCustomAttributes(current => ({ ...current, [attr.attr_id]: value }));
    if (isEmotionAttribute(attr)) setEmotionWord(rawValue);
  }

  function handleAttributeUpdated(updated: AttributeDef) {
    setAttributes(current => current.map(attr => (attr.attr_id === updated.attr_id ? updated : attr)));
  }

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent className="!flex !h-dvh !max-h-dvh !w-dvw !max-w-none flex-col overflow-hidden rounded-none border-0 p-0 sm:!h-auto sm:!max-h-[calc(100dvh-2rem)] sm:!w-[calc(100vw-1rem)] sm:!max-w-[720px] sm:rounded-lg sm:border sm:p-6">
        <DialogHeader className="shrink-0 border-b bg-background/96 px-4 pb-3 pt-[calc(1rem+env(safe-area-inset-top))] pr-12 text-left backdrop-blur sm:border-0 sm:bg-transparent sm:p-0 sm:pr-8">
          <DialogTitle>Log Activity Session</DialogTitle>
          <DialogDescription>
            Record work you already did, even when immersion mode was not running.
          </DialogDescription>
        </DialogHeader>

        <div className="native-scroll grid min-h-0 flex-1 gap-4 overflow-y-auto px-4 py-4 sm:px-0 sm:py-0">
          <div>
            <div className="text-sm font-medium">{task?.title}</div>
            <div className="text-xs text-muted-foreground">
              This updates the activity, the calendar block, and the training data.
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Started">
              <Input type="datetime-local" value={startedAt} onChange={event => setStartedAt(event.target.value)} />
            </Field>
            <Field label="Ended">
              <Input type="datetime-local" value={endedAt} onChange={event => setEndedAt(event.target.value)} />
            </Field>
            <Field label="Duration">
              <div
                className={cn(
                  'flex h-10 items-center rounded-md border px-3 text-sm',
                  durationMinutes ? 'text-foreground' : 'text-red-600'
                )}
              >
                {durationMinutes ? `${durationMinutes} min` : 'Check time'}
              </div>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Overall completion">
              <Input
                type="number"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event => setCompletionPercent(clampNumber(event.target.value, 0, 100) ?? 0)}
              />
            </Field>
            <Field label="Emotion">
              {emotionAttribute ? (
                <EnumAttributeInput
                  attr={emotionAttribute}
                  value={emotionWord}
                  onChange={value => {
                    setEmotionWord(value);
                    updateAttributeValue(emotionAttribute, value);
                  }}
                  onAttributeUpdated={handleAttributeUpdated}
                />
              ) : (
                <Input value={emotionWord} onChange={event => setEmotionWord(event.target.value)} placeholder="thoughtful" />
              )}
            </Field>
            <Field label="Focus">
              <ScoreInput value={actualState.focusLevel} onChange={value => setActualState(s => ({ ...s, focusLevel: value }))} />
            </Field>
          </div>

          {sessionAttributes.length > 0 && (
            <div className="grid gap-2">
              <div>
                <div className="text-sm font-medium">Session attributes</div>
                <div className="text-xs text-muted-foreground">
                  These feed the same specificity model as the activity fields.
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {sessionAttributes.map(attr => (
                  <Field key={attr.attr_id} label={attr.name}>
                    {attr.data_type === 'enum' ? (
                      <EnumAttributeInput
                        attr={attr}
                        value={customAttributes[attr.attr_id] ?? ''}
                        onChange={value => updateAttributeValue(attr, value)}
                        onAttributeUpdated={handleAttributeUpdated}
                      />
                    ) : (
                      <Input
                        type={isNumericAttribute(attr) ? 'number' : 'text'}
                        value={String(customAttributes[attr.attr_id] ?? '')}
                        onChange={event => updateAttributeValue(attr, event.target.value)}
                      />
                    )}
                  </Field>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-5">
            <Field label="P invested">
              <ScoreInput value={actualState.productivityInvestment} onChange={value => setActualState(s => ({ ...s, productivityInvestment: value }))} />
            </Field>
            <Field label="RJ invested">
              <ScoreInput value={actualState.rejuvenationInvestment} onChange={value => setActualState(s => ({ ...s, rejuvenationInvestment: value }))} />
            </Field>
            <Field label="P return">
              <ScoreInput value={actualState.productivityReturn} onChange={value => setActualState(s => ({ ...s, productivityReturn: value }))} />
            </Field>
            <Field label="RJ return">
              <ScoreInput value={actualState.rejuvenationReturn} onChange={value => setActualState(s => ({ ...s, rejuvenationReturn: value }))} />
            </Field>
            <Field label="Resistance">
              <ScoreInput value={actualState.resistance} onChange={value => setActualState(s => ({ ...s, resistance: value }))} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
            <Field label="Flow depth">
              <ScoreInput value={flowDepth} onChange={setFlowDepth} />
            </Field>
            <Field label="Flow note">
              <Input value={flowNote} onChange={event => setFlowNote(event.target.value)} placeholder="When did flow happen?" />
            </Field>
          </div>

          <Field label="Notes">
            <Textarea value={notes} onChange={event => setNotes(event.target.value)} placeholder="What happened during this session?" />
          </Field>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t bg-background/96 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:border-0 sm:bg-transparent sm:p-0">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !durationMinutes || !task}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Log session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function ScoreInput({ value, onChange }: { value: number | null; onChange: (value: number | null) => void }) {
  return (
    <Input
      type="number"
      min={0}
      max={10}
      step={0.5}
      value={value ?? ''}
      onChange={event => onChange(clampNumber(event.target.value, 0, 10))}
    />
  );
}

function isNumericAttribute(attr: AttributeDef): boolean {
  return ['Number', 'float', 'integer'].includes(attr.data_type);
}

function isEmotionAttribute(attr: AttributeDef): boolean {
  return normalizeAttributeName(attr.name) === 'emotion';
}

function normalizeAttributeName(name: unknown): string {
  return String(name ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

function getEmotionValue(
  customAttributes: Record<string, unknown>,
  emotionAttribute?: AttributeDef
): unknown {
  if (emotionAttribute) {
    const direct = customAttributes[emotionAttribute.attr_id];
    if (direct !== null && direct !== undefined && direct !== '') return direct;
  }
  return customAttributes.emotion_word || customAttributes.emotion || customAttributes.emotion_label || '';
}

function clampNumber(value: string, min: number, max: number): number | null {
  if (value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(min, Math.min(max, numeric));
}

function toDateTimeLocal(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
